import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowDown, Loader2 } from "lucide-react";
import { api, apiError, BACKEND_URL } from "../lib/api";

export const RENA = {
  deep: "#022C22",
  forest: "#064E3B",
  gold: "#FACC15",
  off: "#F8FAF8",
  graphite: "#475569",
};

export function useRenalifePage(slug) {
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/public/renalife/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);
  return { data, notFound };
}

export function useRenalifeCta(slug, bono) {
  const [sending, setSending] = useState(false);
  const go = async () => {
    if (sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/public/renalife/${slug}/interest`, { name: "", bono });
      if (data.whatsapp_invite) window.open(data.whatsapp_invite, "_blank");
      else toast.success("¡Listo! El distribuidor te contactará pronto.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };
  return { go, sending };
}

export const Eyebrow = ({ children }) => (
  <div className="font-mono text-xs sm:text-sm tracking-[0.35em] uppercase" style={{ color: `${RENA.gold}B3` }}>{children}</div>
);

export const PageShell = ({ children, testid }) => (
  <div data-testid={testid} className="min-h-screen text-[#F8FAF8]" style={{ background: RENA.deep, fontFamily: "'Manrope', sans-serif" }}>
    {children}
  </div>
);

export const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: RENA.deep }}>
    <Loader2 className="animate-spin" style={{ color: RENA.gold }} size={30} />
  </div>
);

export const NotFoundScreen = () => (
  <div className="min-h-screen flex items-center justify-center text-center px-6" style={{ background: RENA.deep }}>
    <div>
      <div className="font-renalife text-3xl text-[#F8FAF8]">Página no encontrada</div>
      <p className="text-sm mt-2" style={{ color: "#94a3b8" }}>Verifica el enlace que te compartieron.</p>
    </div>
  </div>
);

export const VideoBlock = ({ videoUrl, title }) => {
  if (!videoUrl) return null;
  return (
    <section id="video-bono" className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
      <Eyebrow>El video</Eyebrow>
      <h2 className="font-renalife text-2xl sm:text-3xl mt-3 mb-6">{title}</h2>
      <video data-testid="landing-video" src={videoUrl} controls playsInline className="w-full max-h-[560px] rounded-2xl border bg-black mx-auto" style={{ borderColor: `${RENA.gold}40` }} />
    </section>
  );
};

export const Footer = ({ name }) => (
  <footer className="px-6 py-8 text-center text-[11px]" style={{ color: "#64748b", background: "#01201790" }}>
    Página del distribuidor independiente {name || "RENALIFE"} · RENALIFE MÉXICO · Los ejemplos de cálculo no constituyen una promesa de ingresos.
  </footer>
);

export default function RenalifeLanding() {
  const { slug } = useParams();
  const { data, notFound } = useRenalifePage(slug);
  const { go, sending } = useRenalifeCta(slug, "bono1");
  const [people, setPeople] = useState(10);

  if (notFound) return <NotFoundScreen />;
  if (!data) return <LoadingScreen />;

  const img = data.assets?.bono1_inicio_rapido?.image_url;
  const video = data.assets?.bono1_inicio_rapido?.video_url;

  return (
    <PageShell testid="renalife-landing-bono1">
      {/* 1) HERO 100vh */}
      <section className="min-h-screen flex items-center px-6 sm:px-12" style={{ background: `linear-gradient(160deg, ${RENA.deep} 0%, ${RENA.forest} 120%)` }}>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center py-16 w-full">
          <div>
            <Eyebrow>BONO 001 · INSTANTÁNEO</Eyebrow>
            <h1 className="font-renalife font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.08] mt-5">
              Gana <em className="italic font-medium" style={{ color: RENA.gold }}>$500 pesos</em> cada vez que alguien confía en ti.
            </h1>
            <p className="text-base sm:text-lg mt-6 max-w-xl" style={{ color: "#cbd5d1" }}>
              El Bono Inicio Rápido de Renalife México te paga $500 MXN en el momento — no en el corte mensual —
              cada vez que un nuevo distribuidor activa su kit de $3,200 MXN directamente contigo.
            </p>
            <div className="flex flex-wrap items-center gap-5 mt-9">
              <button
                data-testid="cta-register"
                onClick={go}
                disabled={sending}
                className="rounded-full px-8 py-4 font-bold text-base transition-transform hover:-translate-y-0.5"
                style={{ background: RENA.gold, color: RENA.deep }}
              >
                {sending ? "Abriendo WhatsApp…" : "Registrarme como distribuidor"}
              </button>
              {video && (
                <a data-testid="cta-video" href="#video-bono" className="inline-flex items-center gap-2 text-sm font-semibold hover:opacity-80" style={{ color: RENA.gold }}>
                  Ver el video de 20 segundos <ArrowDown size={15} />
                </a>
              )}
            </div>
          </div>
          {img && (
            <div className="relative">
              <img data-testid="hero-image" src={`${BACKEND_URL}${img}`} alt="Bono Inicio Rápido" className="rounded-3xl border shadow-2xl w-full object-cover" style={{ borderColor: `${RENA.gold}30` }} />
            </div>
          )}
        </div>
      </section>

      {/* 2) CÓMO FUNCIONA */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 py-20">
        <Eyebrow>Cómo funciona</Eyebrow>
        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {[
            { n: "01", t: "Comparte tu link único.", s: "Cada distribuidor tiene un link personal que va conectado a tu red." },
            { n: "02", t: "Alguien compra su kit de $3,200 MXN.", s: "Renalife, Amazonia Café o Combo Premium. El bono es el mismo." },
            { n: "03", t: "$500 MXN llegan a tu cuenta el mismo día.", s: "Sin esperar corte de mes, sin condiciones ocultas." },
          ].map((c) => (
            <div key={c.n} data-testid={`step-${c.n}`} className="rounded-2xl border p-7" style={{ borderColor: `${RENA.gold}25`, background: "#03251c" }}>
              <div className="font-mono text-sm" style={{ color: RENA.gold }}>{c.n}</div>
              <h3 className="font-renalife text-xl mt-3">{c.t}</h3>
              <p className="text-sm mt-2" style={{ color: "#a7bab3" }}>{c.s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3) MATH IS BEAUTIFUL */}
      <section className="px-6 sm:px-12 py-20" style={{ background: "#03251c" }}>
        <div className="max-w-3xl mx-auto text-center">
          <Eyebrow>Math is beautiful</Eyebrow>
          <h2 className="font-renalife text-2xl sm:text-3xl mt-4">¿Cuántas personas invitarías en tu primer mes?</h2>
          <div className="mt-10">
            <input
              data-testid="calc-slider"
              type="range"
              min={1}
              max={20}
              value={people}
              onChange={(e) => setPeople(Number(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer"
              style={{ accentColor: RENA.gold, background: `${RENA.gold}30` }}
            />
            <div className="flex justify-between text-xs font-mono mt-2" style={{ color: "#64748b" }}>
              <span>1</span><span>20</span>
            </div>
          </div>
          <div className="font-mono text-lg mt-8" style={{ color: "#cbd5d1" }}>{people} persona{people > 1 ? "s" : ""}</div>
          <div data-testid="calc-result" className="font-renalife text-3xl sm:text-5xl mt-3">
            Ganarías <span style={{ color: RENA.gold }}>${(people * 500).toLocaleString("es-MX")} MXN</span> solo por Inicio Rápido este mes.
          </div>
          <p className="text-xs mt-6" style={{ color: "#64748b" }}>
            Estos son ejemplos de cálculo, no una promesa de ingresos. Tus resultados dependen de tu esfuerzo.
          </p>
        </div>
      </section>

      <VideoBlock videoUrl={video} title="El Bono Inicio Rápido en 20 segundos" />

      {/* 4) TESTIMONIO + CTA FINAL */}
      <section className="max-w-4xl mx-auto px-6 sm:px-12 py-20 text-center">
        <blockquote className="font-renalife text-2xl sm:text-4xl leading-snug">
          “Registré a mi cuñada un miércoles a las 10 de la noche. El bono llegó antes de que yo me durmiera.”
        </blockquote>
        <div className="font-mono text-sm mt-6" style={{ color: "#94a3b8" }}>— Karla M., 34 años, Guadalajara.</div>
      </section>
      <button
        data-testid="cta-final"
        onClick={go}
        disabled={sending}
        className="w-full py-8 font-renalife font-bold text-2xl sm:text-3xl transition-opacity hover:opacity-90 flex items-center justify-center gap-3"
        style={{ background: RENA.gold, color: RENA.deep }}
      >
        Empieza tu red hoy <ArrowRight size={26} />
      </button>
      <Footer name={data.distributor_name} />
    </PageShell>
  );
}
